<template>
  <div>
    <f-data-table id="feeds-table"
                  :heading="`${getFeedLabel(true)}`"
                  :server-side="true"
                  :data-uri="data_uri"
                  :static-params="[
                    {
                      field: `with_player_data`,
                      value: `true`,
                    },
                  ]"
                  :columns="columns"
                  :selectable-rows="true"
                  :locked="true"
                  icon="fa fa-rss">
      <template #_actions="props">
        <a v-if="has_feed_types"
           type="button"
           class="btn btn-ss-standout btn-icon-sm"
           href="/feeds/create/">
          <i class="flaticon2-plus" />New {{ getFeedLabel(true) }}
        </a>
        <button v-else
                type="button"
                class="btn btn-ss-standout btn-icon-sm"
                disabled
                title="No feed types available for your customer; please contact Sharpstream support.">
          <i class="flaticon2-plus" />New Feed
        </button>
        <button v-if="can_import_feed" type="button"
                class="btn btn-ss btn-icon-sm"
                @click="props.do({ cb: importFeed })">
          <i class="la la-download" />
          Import
        </button>
        <button type="button"
                class="btn btn-ss btn-icon-sm"
                @click="props.reloadData()">
          <i class="flaticon2-refresh" />Reload Data
        </button>
      </template>
      <template #_filters>
        <f-data-table-filters target="feeds-table"
                              :form-columns="2"
                              :expanded="false"
                              filter-layout="col">
          <template #name="props">
            <f-data-table-filter field="name" display="Name:"
                                 :scope="props" />
          </template>
          <template #description="props">
            <f-data-table-filter field="description" display="Description:"
                                 :scope="props" />
          </template>
          <template #type="props">
            <f-data-table-filter>
              <label for="type-filter">Type:</label>
              <multiselect v-model="feed_type_multi_select_val"
                           name="published-status-select"
                           :options="[ `Simple`, `Live` ]"
                           placeholder="Select a status..."
                           @input="(selected) => {
                             if (selected) {
                               selected = selected.toLowerCase();
                             }
                             props.filterChange({
                               client: {
                                 op: (val) => {
                                   if (!selected) {
                                     return true;
                                   } else {
                                     return selected === val;
                                   }
                                 },
                                 field: `type`
                               },
                               server: {
                                 type: `param`,
                                 field: `type`,
                                 value: selected,
                               }
                             })
                           }" />
            </f-data-table-filter>
          </template>
          <template #tags="props">
            <f-data-table-filter>
              <label for="tags-filter">Tags:</label>
              <multiselect v-model="tags_input_val"
                           :options="tags_ac_list"
                           :multiple="true"
                           @input="() => {
                             props.filterChange({
                               server: {
                                 type: `param`,
                                 field: `tags`,
                                 value: tags_input_val.join(`,`)
                               }
                             });
                           }" />
            </f-data-table-filter>
          </template>
        </f-data-table-filters>
      </template>
      <template #_row_controls="props">
        <button class="btn btn-ss"
                type="button"
                :disabled="any_loading"
                @click="props.do({ cb: confirmSyncFeedItemsByTags, target: selected_feeds, reload_data: false, })">
          <loader v-if="loading.sync_items" small class="mr-2" />
          <i v-else class="la la-tags" />Sync {{ getItemLabel(true) }}s by Feed Tags
        </button>
        <button class="btn btn-ss"
                type="button"
                :disabled="any_loading"
                @click="props.do({ cb: confirmSyncFeedTagsToFeedItems, target: selected_feeds, reload_data: false })">
          <loader v-if="loading.sync_tags" small class="mr-2" />
          <i v-else class="la la-tags" />Sync Feed Tags to {{ getItemLabel(true) }}s
        </button>
        <button class="btn btn-ss"
                type="button"
                :disabled="any_loading"
                @click="props.do({ cb: duplicate })">
          <loader v-if="loading.duplicate" small class="mr-2" />
          <i v-else class="la la-copy" />Duplicate
        </button>
        <button class="btn btn-ss"
                type="button"
                :disabled="any_loading"
                @click="props.do({ cb: publish })">
          <loader v-if="loading.publish" small class="mr-2" />
          <i v-else class="la la-cloud-upload" />Publish
        </button>
        <button class="btn btn-ss"
                type="button"
                :disabled="any_loading"
                @click="props.do({ cb: updateFeedFiles, reload_data: false })">
          <loader v-if="loading.update_files" small class="mr-2" />
          <i v-else class="la la-refresh" />Update Feed Files
        </button>
        <button class="btn btn-ss-danger"
                type="button"
                :disabled="any_loading"
                @click="props.do({ cb: confirmDestroyFeeds, reload_data: true })">
          <loader v-if="loading.delete" small class="mr-2" />
          <i v-else class="flaticon2-warning" />Delete
        </button>
      </template>
      <template #feed_artwork_thumbnail_url="props">
        <img :src="resolveFeedArtworkUrl(props.row)"
             alt="Thumb"
             class="thumbnail rounded"
             style="width: 64px;">
      </template>

      <template #description="props">
        <read-more :text="props.row.description" more-str="[ Expand ]"
                   less-str="[ Hide ]"
                   :max-chars="40" />
      </template>

      <template #premium="props">
        <div v-if="props.row.premium.length > 0">
          <div v-for="(premium, index) in props.row.premium" :key="index">
            <a target="_blank" :href="`live-feeds/${premium.uuid}`">{{ premium.name }} </a>
          </div>
        </div>
        <div v-else>
          None
        </div>
      </template>

      <template #tags="props">
        <multi-select-tag-bag :tags-list="props.row.tags.map(tag => tag.name)" :limit="5" />
      </template>
      <template #created_at="props">
        <span>{{ formatDateTime(props.value) }}</span>
      </template>
      <template #actions="props">
        <div class="row-action-cell">
          <b-dropdown split
                      text="Edit"
                      :split-href="`/feeds/${props.row.uuid}/edit`">
            <b-dropdown-item-button @click="props.do({ cb: () => showItemQuickModal(props.row), reload_data: false })">
              <i class="la la-copy" />Assign New Item
            </b-dropdown-item-button>
            <b-dropdown-item :href="`${props.row.feed_xml_file_url}`" target="_blank">
              <i class="la la-eye" />Feed RSS
            </b-dropdown-item>
            <b-dropdown-item-button @click="props.do({ cb: duplicate })">
              <i class="la la-copy" />Duplicate
            </b-dropdown-item-button>
            <b-dropdown-divider />
            <b-dropdown-item-button @click="shareFeed(props.row)">
              <i class="fa fa-share-alt" />Share
            </b-dropdown-item-button>
            <b-dropdown-item-button @click="previewFeed(props.row)">
              <i class="fa fa-play" />Preview
            </b-dropdown-item-button>

            <b-dropdown-item-button @click="props.do({ cb: () => confirmSyncFeedItemsByTags(props.row), reload_data: false })">
              <i class="la la-tags" />Sync {{ getItemLabel(true) }}s by Feed Tags
            </b-dropdown-item-button>
            <b-dropdown-item-button @click="props.do({ cb: () => confirmSyncFeedTagsToFeedItems(props.row), reload_data: false })">
              <i class="la la-tags" />Sync Feed Tags to {{ getItemLabel(true) }}s
            </b-dropdown-item-button>
            <b-dropdown-divider />
            <b-dropdown-item-button variant="ss-danger" @click="props.do({ cb: confirmDestroyFeeds })">
              <i class="flaticon2-warning" />Delete
            </b-dropdown-item-button>
          </b-dropdown>
        </div>
      </template>
    </f-data-table>

    <delete-confirm-modal name="delete-confirm"
                          :entities-to-delete="selected_feeds"
                          entity-type="feed"
                          :callback="modalCallback" />
    <social-share-modal id="share-feed-confirm" name="share-feed" :entity="modal_action_feed" />
    <preview-modal name="preview-feed" :is-feed="true" :entity="modal_action_feed" />
    <sync-feed-tags-to-items-modal name="sync-tags-items"
                                   :feeds="selected_feeds"
                                   :callback="modalCallback" />
    <sync-items-to-feed-by-tag-modal name="sync-items-tags"
                                     :feeds="selected_feeds"
                                     :callback="modalCallback" />
    <quick-alert-modal v-bind="quick_alert" />
    <feed-url-modal />
    <item-creation-modal :preselected-feed="selected_feeds[0]" />
  </div>
</template>

<script>
import FDataTable from 'VuePartials/App/Storage/FDataTable/FDataTable';
import FDataTableFilters from 'VuePartials/App/Storage/FDataTable/FDataTableFilters';
import FDataTableFilter from 'VuePartials/App/Storage/FDataTable/FDataTableFilter';
import ReadMore from "VueComponents/ReadMoreComponent";
import DeleteConfirmModal from "VueModals/DeleteConfirmModal";
import SocialShareModal from "VueModals/SocialShareModal";
import PreviewModal from 'VueModals/PreviewModal';
import SyncFeedTagsToItemsModal from 'VueModals/SyncFeedTagsToItemsModal';
import SyncItemsToFeedByTagModal from 'VueModals/SyncItemsToFeedByTagModal';
import MultiSelectTagBag from 'VueComponents/MultiSelectTagBag';
import {
  BDropdown,
  BDropdownItem,
  BDropdownItemButton,
  BDropdownDivider,
} from 'bootstrap-vue';
import FDataTableMixin from 'VueMixins/FDataTableMixin';
import FeedsApiMixin from 'VueMixins/Feeds/FeedsApiMixin';
import FeedsModalsMixin from 'VueMixins/Feeds/FeedsModalsMixin';
import FeedUrlModal from 'VuePartials/Feeds/FeedUrlModal';
import ItemCreationMixin from 'VueMixins/ItemQuickCreateMixin';

export default {
  components: {
    FDataTable,
    FDataTableFilters,
    FDataTableFilter,
    ReadMore,
    DeleteConfirmModal,
    SocialShareModal,
    PreviewModal,
    SyncFeedTagsToItemsModal,
    SyncItemsToFeedByTagModal,
    MultiSelectTagBag,
    BDropdown,
    BDropdownItem,
    BDropdownItemButton,
    BDropdownDivider,
    FeedUrlModal,
  },
  mixins: [
    FDataTableMixin,
    FeedsApiMixin,
    FeedsModalsMixin,
    ItemCreationMixin,
  ],
  data: () => ({
    tags: window.tags,
    selected_tags: [],
    selected_type_filter: null,
    selections_are_live: false,
    modalProps: { row: { name: "", description: "" } },
    columns: [
      {
        field: `feed_artwork_thumbnail_url`,
        display: `Thumbnail`,
        sortable: false,
        width: 70,
        clip_resistance: -1,
      },
      {
        field: `name`,
        display: `Name`,
        sort_dir: `asc`,
      },
      {
        field: `description`,
        display: `Description`,
      },
      {
        display: 'Live feeds',
        field: `premium`,
        sortable: false,
      },
      {
        field: `tags`,
        display: `Tags`,
        sortable: false,
      },
      {
        field: `created_at`,
        display: `Created at`,
        active_sort: true,
      },
      {
        field: `actions`,
        display: `Actions`,
        width: 150,
        never_clip: true,
        sortable: false,
      },
    ],
    feed_type_multi_select_val: ``,
    tags_input_val: [],
    tags_ac_list: [],
  }),
  computed: {
    any_loading() {
      return Object.values(this.loading).some(flag => flag === true);
    },
    has_feed_types() {
      return window.customer_feed_types.length > 0;
    },
  },
  watch: {
    selected_type_filter: function (newValue) {
      if (newValue && newValue.value) {
        this.additional_params.type = newValue.value;
      } else {
        delete this.additional_params.type;
      }

      this.updateData();
    },
    selected_tags: function (new_values) {
      if (new_values.length) {
        const tags = _.map(new_values, 'name');
        this.additional_params.tags = tags.join(',');
      } else {
        delete this.additional_params.tags;
      }

      this.updateData();
    },
  },
  mounted() {
    EventBus.$on(`fdt-rows-selected`, (ev) => {
      this.selected_feeds = ev;
    });
    this.tags_ac_list = window.tags.map(tag => tag.name);
  },
  methods: {
    async duplicate(...feeds) {
      this.loading.duplicate = true;
      await this.duplicateFeeds(...feeds);
      this.loading.duplicate = false;
    },
    async publish(...feeds) {
      this.loading.publish = true;
      await this.publishFeeds(...feeds);
      this.loading.publish = false;
    },
  },
};
</script>

<style lang='scss' scoped>
  @use "~AssetsSass/_mixins" as *;

  @include metronic-like-vti;

  i[class*='flaticon'] {
    &::before {
      margin-right: 0.4em;
    }
  }
  :deep(&.dropdown-menu > .dropdown-item, .dropdown-menu > li > a) {
    padding: .25rem 1.5rem;
    i {
      margin-right: 2px;
    }
  }
  .row-action-cell {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .vue-tags-input {
    width: 100%;
  }
</style>
